'use client'

import type {
  Instrument,
  InstrumentType,
  ProposalOrder,
} from '@moment/horizon-data-access/proposal'
import type { CellClickedEvent } from 'ag-grid-community'
import type { LiveInventoryItem } from '@/types/api/live-inventory'

import { IsoDateStr } from '@moment/domain-types'
import { InstrumentStatus } from '@moment/horizon-data-access/proposal'
import { toError } from '@moment/observability/logger'
import { type FC, useCallback, useMemo, useState } from 'react'

import Button, { ButtonVariant } from '@/components/atomic/button'
import DialogSplit from '@/components/atomic/dialog-split'
import Icon, { IconName } from '@/components/atomic/icon'
import Input from '@/components/atomic/input'
import { LoadingIcon } from '@/components/atomic/loading-icon'
import { BodyDefault, BodySmall } from '@/components/atomic/text'
import ProposalBondSwapTable from '@/components/molecules/proposal-bond-swap-table'
import BondPreview from '@/components/pages/bond-preview'
import ProposalOrderPreview from '@/components/pages/proposal-order-preview'
import {
  useGetReplacementCandidatesQuery,
  useUpdateProposalOrderMutation,
} from '@/legacy-api/proposals-slice'
import { createLogger } from '@/lib/logger'
import { useOrdersWithSecurityInstruments } from '@/portfolio/proposal-builder/hooks/use-orders-with-security-instruments'
import { BondSearchInput } from '@/shared/components/bond-search-input'
import { OrderSide, OrderStatus } from '@/types/enums/order'
import { convertNumBondsToParValue } from '@/utils/presentation/bonds'
import { cn } from '@/utils/shadcn/utils'

import {
  AlternativeBondsContainer,
  AlternativeBondsTableWrapper,
  BondSwapContainer,
  BondTableWrapper,
  DialogActionButtons,
  DialogContentContainer,
  DialogPreviewBorder,
  DialogPreviewContainer,
  EmptyState,
  TopBondTableContainer,
} from './styles'

const LOGGER = createLogger('components/molecules/proposal-bond-swap-dialog')

// Preview-only instrument for the temporary ProposalOrder shown in the right
// panel; never persisted. Bonds surfaced here are live inventory, so status is
// always outstanding and the FE BondType narrows to the generated InstrumentType.
function buildPreviewInstrument(
  bond: LiveInventoryItem,
  { evalPrice, evalYtw }: { evalPrice: number; evalYtw: number },
): Instrument {
  // Live-inventory maturity dates are raw API strings, so an unexpected shape
  // degrades to an absent maturity rather than throwing in the click handler.
  const maturityDate = IsoDateStr.safeParse(bond.maturity_date)

  return {
    isin: bond.isin,
    cusip: bond.cusip,
    issuer: bond.issuer,
    description: bond.description,
    description_short: bond.description_short ?? undefined,
    sp_rating: bond.sp_rating,
    moodys_rating: bond.moodys_rating || null,
    sector: bond.sector || '',
    par_value: bond.par_value,
    duration: bond.duration || 0,
    coupon: bond.coupon ?? 0,
    accrued_interest: bond.accrued_interest ?? 0,
    principal_factor: bond.principal_factor ?? 1,
    close_price: bond.close_price ?? 0,
    close_yield_to_maturity: bond.close_ytm ?? 0,
    close_yield_to_worst: bond.close_ytw ?? 0,
    next_call_price: bond.next_call_price ?? 0,
    yield_to_worst: evalYtw,
    price: evalPrice,
    yield_to_maturity: evalYtw,
    maturity_date: maturityDate.success ? maturityDate.data : undefined,
    status: InstrumentStatus.outstanding,
    type: bond.type as `${InstrumentType}`,
  }
}

interface ProposalBondSwapDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirmSwap?: (selectedBond: ProposalOrder) => void
  proposalId: string
  order: ProposalOrder
}

const SearchBondsContainer: React.FC<
  React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>
> = ({ className, children, ...props }) => {
  return (
    <div className={cn('flex flex-col space-y-2', className)} {...props}>
      {children}
    </div>
  )
}

const SearchBondsWrapper: React.FC<
  React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>
> = ({ className, children, ...props }) => {
  return (
    <div
      className={cn('border border-primary-border rounded-lg p-1', className)}
      {...props}
    >
      {children}
    </div>
  )
}

const ProposalBondSwapDialog: FC<ProposalBondSwapDialogProps> = ({
  open,
  onOpenChange,
  onConfirmSwap,
  proposalId,
  order,
}) => {
  const [selectedBond, setSelectedBond] = useState<ProposalOrder | null>(null)
  const [selectedBonds, setSelectedBonds] = useState<ProposalOrder[]>([])
  const [searchBondValue, setSearchBondValue] = useState<string>('')
  const [searchedBond, setSearchedBond] = useState<LiveInventoryItem | null>(
    null,
  )
  const [searchedBondQuantity, setSearchedBondQuantity] = useState<string>('')
  const [updateOrder, { isLoading: isUpdating }] =
    useUpdateProposalOrderMutation()

  const {
    data: replacementCandidates,
    isLoading: isLoadingCandidates,
    isFetching: isFetchingCandidates,
  } = useGetReplacementCandidatesQuery({
    proposal_id: proposalId,
    moment_order_id: order.moment_order_id,
    limit: 100,
  })

  // Extract orders from replacement candidates
  const candidateOrders = useMemo<ProposalOrder[]>(() => {
    if (!replacementCandidates) return []
    return replacementCandidates.candidates.map((candidate) => candidate.order)
  }, [replacementCandidates])
  const {
    orders: replacementOrders,
    isLoading: isLoadingInstruments,
    error: instrumentsError,
  } = useOrdersWithSecurityInstruments(candidateOrders)

  const hasReplacementOrders = useMemo(() => {
    return replacementOrders.length > 0
  }, [replacementOrders])

  // Filter out selected bonds from the alternative bonds table
  const filteredReplacementOrders = useMemo(() => {
    if (selectedBonds.length === 0) return replacementOrders

    // Filter out any bond that is already selected and shown in the top table
    return replacementOrders.filter(
      (bond) =>
        !selectedBonds.some(
          (selectedBond) =>
            selectedBond.moment_order_id === bond.moment_order_id,
        ),
    )
  }, [replacementOrders, selectedBonds])

  const handleBondSelect = useCallback(
    (event: CellClickedEvent) => {
      const selectedData = event.data

      // If the clicked bond is not the original order
      if (selectedData.moment_order_id !== order.moment_order_id) {
        // Replace any existing alternative selection with the new bond
        // But keep it as a single-item array to ensure we always show it with the original bond
        setSelectedBonds([selectedData])
        // Update the selectedBond for the preview panel
        setSelectedBond(selectedData)
      } else {
        // If original order is clicked, clear alternative selections
        setSelectedBonds([])
        // Set the selectedBond as the original order
        setSelectedBond(order)
      }
    },
    [order],
  )

  const handleRemoveBond = useCallback(
    (_bondId: string) => {
      // Clear the selectedBonds array
      setSelectedBonds([])

      // Reset the selected bond to the original order
      setSelectedBond(order)
    },
    [order],
  )

  const handleSearchBondSelect = useCallback((bond: LiveInventoryItem) => {
    // Store the searched bond and update the search value
    setSearchedBond(bond)
    setSearchBondValue(bond.isin || bond.cusip || '')

    // Set the selected bond to show the order book in the right panel
    // Create a temporary ProposalOrder for preview purposes
    const tempProposalOrder: ProposalOrder = {
      moment_order_id: `temp_${bond.isin}_${Date.now()}`,
      instrument_id: bond.isin,
      // Eval values - use current market data from instrument level
      instrument: buildPreviewInstrument(bond, {
        evalPrice: bond.close_price || 0,
        evalYtw: bond.close_ytw || 0,
      }),
      // Limit values - will be set when user selects from order book
      limit_price: 0,
      limit_ytw: 0,
      limit_ytm: 0,
      quantity: 0,
      allocations: [],
      side: OrderSide.Buy,
      status: OrderStatus.PendingApproval,
      filled_quantity: 0,
      filled_avg_price: 0,
    }

    // Set this as the selected bond to show in the right panel
    setSelectedBond(tempProposalOrder)
  }, [])

  const handleAddSearchedBond = useCallback(() => {
    if (!searchedBond || !searchedBondQuantity) return

    const quantityInBonds = parseFloat(searchedBondQuantity)
    // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
    if (isNaN(quantityInBonds) || quantityInBonds <= 0) return

    // Convert number of bonds to par value for the API
    const quantityInParValue = convertNumBondsToParValue(quantityInBonds)

    // Get eval values from current market data (instrument level)
    const evalPrice = searchedBond.close_price || 0
    const evalYTW = searchedBond.close_ytw || 0

    // Get limit values from the selected order book row (if any) or use best ask
    const currentSelectedBond = selectedBond // Get the current selected bond with limit values
    let limitPrice =
      currentSelectedBond?.limit_price ||
      searchedBond.asks?.[0]?.price ||
      evalPrice
    let limitYTW =
      currentSelectedBond?.limit_ytw ||
      searchedBond.asks?.[0]?.yield_to_worst ||
      evalYTW

    // Ensure limit values are positive and valid
    if (limitPrice <= 0) {
      limitPrice = evalPrice > 0 ? evalPrice : 100 // Fallback to 100 if no valid price
    }
    if (limitYTW <= 0) {
      limitYTW = evalYTW > 0 ? evalYTW : 5.0 // Fallback to 5% if no valid YTW
    }

    // Convert LiveInventoryItem to ProposalOrder format
    const newProposalOrder: ProposalOrder = {
      moment_order_id: `search_${searchedBond.isin}_${Date.now()}`, // Generate unique ID
      instrument_id: searchedBond.isin,
      // Eval values - use current market data from instrument level
      // (YTW approximates YTM)
      instrument: buildPreviewInstrument(searchedBond, {
        evalPrice,
        evalYtw: evalYTW,
      }),
      // Limit values - ensure they are positive
      limit_price: limitPrice,
      limit_ytw: limitYTW,
      limit_ytm: limitYTW, // Use YTW as YTM approximation
      quantity: quantityInParValue, // Use par value for the API
      allocations: order.allocations, // Use same allocations as original order
      side: OrderSide.Buy,
      status: OrderStatus.PendingApproval,
      filled_quantity: 0,
      filled_avg_price: 0,
      tax_effective_limit_ytw: order.tax_effective_limit_ytw,
      post_tax_limit_ytw: order.post_tax_limit_ytw,
    }

    // Replace any existing searched bonds with the new one
    setSelectedBonds([newProposalOrder])

    // Update the selectedBond to point to the newly added bond (not the temporary one)
    setSelectedBond(newProposalOrder)

    // Clear only the search input state, but keep the bond preview open
    setSearchedBond(null)
    setSearchedBondQuantity('')
    setSearchBondValue('')
  }, [searchedBond, searchedBondQuantity, order, selectedBond])

  const handleClearSearch = useCallback(() => {
    setSearchedBond(null)
    setSearchedBondQuantity('')
    setSearchBondValue('')
  }, [])

  const handleCloseDialog = useCallback(() => {
    setSelectedBond(null)
    setSelectedBonds([])
    onOpenChange(false)
  }, [onOpenChange])

  const handleConfirmSwap = useCallback(async () => {
    if (selectedBond) {
      try {
        // Update the proposal order with the new bond's limit price
        await updateOrder({
          proposal_id: proposalId,
          proposal_order_id: order.moment_order_id,
          limit_price: selectedBond.limit_price || 0,
          limit_ytm: selectedBond.limit_ytm ?? null,
          limit_ytw: selectedBond.limit_ytw ?? null,
          tax_effective_limit_ytw: selectedBond.tax_effective_limit_ytw ?? null,
          post_tax_limit_ytw: selectedBond.post_tax_limit_ytw ?? null,
          instrument_id: selectedBond.instrument_id || '',
          allocations: selectedBond.allocations || [],
        }).unwrap()

        // Call the parent component's onConfirmSwap callback
        onConfirmSwap?.(selectedBond)
        handleCloseDialog()
      } catch (error) {
        LOGGER.error('Failed to update proposal order', {
          error: toError(error),
        })
      }
    }
  }, [
    selectedBond,
    onConfirmSwap,
    handleCloseDialog,
    updateOrder,
    proposalId,
    order.moment_order_id,
  ])

  // Combine the current order and selected bonds for the top table
  const topTableOrders = useMemo(() => {
    // Always include the original order first, then any selected bonds
    return [order, ...selectedBonds]
  }, [order, selectedBonds])

  const mainContent = useMemo(() => {
    let alternativeBonds = (
      <EmptyState text="No alternative options found for the current bond" />
    )
    if (isLoadingCandidates || isFetchingCandidates || isLoadingInstruments) {
      alternativeBonds = (
        <div className="flex items-center justify-center h-full">
          <LoadingIcon />
        </div>
      )
    } else if (instrumentsError) {
      alternativeBonds = <EmptyState text="Alternative bonds failed to load" />
    } else if (hasReplacementOrders) {
      alternativeBonds = (
        <ProposalBondSwapTable
          proposalId={proposalId}
          onBondClick={handleBondSelect}
          customOrders={filteredReplacementOrders}
          isAlternativeBondsTable={true}
        />
      )
    }

    return (
      <BondSwapContainer>
        <TopBondTableContainer className="p+4">
          <BondTableWrapper>
            <ProposalBondSwapTable
              proposalId={proposalId}
              disablePagination
              customOrders={topTableOrders}
              onBondClick={handleBondSelect}
              onRemoveBond={handleRemoveBond}
              originalOrderId={order.moment_order_id}
              isAlternativeBondsTable={false}
              disableOriginalBond
            />
          </BondTableWrapper>
        </TopBondTableContainer>

        <AlternativeBondsContainer className="mt-4 mb-4 flex-grow">
          <BodySmall className="text-secondary">Alternative bonds</BodySmall>
          <AlternativeBondsTableWrapper>
            {alternativeBonds}
          </AlternativeBondsTableWrapper>
        </AlternativeBondsContainer>

        {/* Search Section with both inputs on one card */}
        <div className="mt-4 mb-2">
          <BodySmall className="text-secondary">Search for a bond</BodySmall>
        </div>
        <SearchBondsContainer className="mb-4">
          <SearchBondsWrapper>
            <div className="flex items-end gap-3">
              {/* Bond Search Input */}
              <div className="flex-1">
                <BondSearchInput
                  value={searchBondValue}
                  onChange={setSearchBondValue}
                  size="small"
                  onBondSelect={handleSearchBondSelect}
                  label=""
                />
              </div>

              {/* Quantity Input */}
              <div className="w-32">
                <Input
                  size="small"
                  label=""
                  type="number"
                  placeholder="Qty"
                  value={searchedBondQuantity}
                  onChange={(e) => setSearchedBondQuantity(e.target.value)}
                  step="1"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <Button
                  variant={ButtonVariant.Primary}
                  onClick={handleAddSearchedBond}
                  disabled={
                    !searchedBond ||
                    !searchedBondQuantity ||
                    parseFloat(searchedBondQuantity) <= 0
                  }
                  label="Add"
                />
                {(searchedBond || searchedBondQuantity) && (
                  <Button
                    variant={ButtonVariant.Secondary}
                    onClick={handleClearSearch}
                    label="Clear"
                  />
                )}
              </div>
            </div>
          </SearchBondsWrapper>
        </SearchBondsContainer>

        <DialogActionButtons>
          <Button
            label="Cancel"
            variant={ButtonVariant.Secondary}
            onClick={handleCloseDialog}
          />
          <Button
            label="Confirm swap"
            variant={ButtonVariant.Primary}
            onClick={handleConfirmSwap}
            disabled={selectedBonds.length === 0 || isUpdating}
            isLoading={isUpdating}
          />
        </DialogActionButtons>
      </BondSwapContainer>
    )
  }, [
    handleCloseDialog,
    handleConfirmSwap,
    proposalId,
    topTableOrders,
    handleBondSelect,
    handleRemoveBond,
    order.moment_order_id,
    isUpdating,
    filteredReplacementOrders,
    hasReplacementOrders,
    isLoadingCandidates,
    isFetchingCandidates,
    isLoadingInstruments,
    instrumentsError,
    searchBondValue,
    handleSearchBondSelect,
    searchedBond,
    searchedBondQuantity,
    handleAddSearchedBond,
    handleClearSearch,
    selectedBonds.length,
  ])

  // Add handler to close the bond preview
  const handleCloseBondPreview = useCallback(() => {
    setSelectedBond(null)
    setSearchedBond(null)
    setSearchBondValue('')
    setSearchedBondQuantity('')
  }, [])

  // Modify the rightContent to show full bond preview when a searched bond is selected
  const rightContent = useMemo(() => {
    if (!selectedBond) return null

    // If this is a temporary bond from search OR a newly added bond from search
    if (
      selectedBond.moment_order_id.startsWith('temp_') ||
      selectedBond.moment_order_id.startsWith('search_')
    ) {
      return (
        <DialogPreviewContainer>
          <DialogPreviewBorder>
            <div className="flex flex-col h-full">
              <div className="shrink-0 px-4 py-2 flex items-center justify-between">
                <div>
                  <BodyDefault className="font-bold">
                    {selectedBond.instrument.description_short}
                  </BodyDefault>
                  <BodySmall className="text-secondary">
                    {selectedBond.instrument.cusip ||
                      selectedBond.instrument.isin}
                  </BodySmall>
                </div>
                {/** biome-ignore lint/a11y/useButtonType: Bulk suppression as part of biome migration */}
                <button
                  onClick={handleCloseBondPreview}
                  className="p-1 hover:bg-secondary-background rounded cursor-pointer"
                  aria-label="Close bond preview"
                >
                  <Icon iconName={IconName.XMarkIcon} className="w-4 h-4" />
                </button>
              </div>
              <div className="grow overflow-y-auto">
                <BondPreview id={selectedBond.instrument.isin} />
              </div>
            </div>
          </DialogPreviewBorder>
        </DialogPreviewContainer>
      )
    }

    // Otherwise show the normal proposal order preview
    return (
      <DialogPreviewContainer>
        <DialogPreviewBorder>
          <ProposalOrderPreview
            proposalId={proposalId}
            replacementCandidates={replacementCandidates?.candidates || []}
            selectedOrderId={selectedBond.moment_order_id}
            disableQuantityEditing
            hideCancelAllocation
            onClose={handleCloseDialog}
          />
        </DialogPreviewBorder>
      </DialogPreviewContainer>
    )
  }, [
    selectedBond,
    proposalId,
    replacementCandidates?.candidates,
    handleCloseBondPreview,
    handleCloseDialog,
  ])

  return (
    <DialogSplit
      isSplitView
      title="Swap bond"
      leftContent={
        <DialogContentContainer>
          <BodyDefault className="font-bold">
            {replacementCandidates?.candidates?.length
              ? `Explore ${replacementCandidates.candidates.length} similar alternative bonds`
              : 'No alternative bonds available'}
          </BodyDefault>
          <BodySmall className="text-secondary">
            {replacementCandidates?.candidates?.length
              ? 'All options will satisfy the same compliance checks and be allocated to the same set of accounts as the current bond.'
              : 'There are currently no alternative bonds that satisfy the required compliance checks.'}
          </BodySmall>
          {mainContent}
        </DialogContentContainer>
      }
      rightContent={rightContent}
      open={open}
      onOpenChange={onOpenChange}
      dialogContentClassName="max-w-[90%] h-[90vh]"
    />
  )
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default ProposalBondSwapDialog
